import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { MapPin, Truck, Star, MessageSquarePlus } from "lucide-react";
import Messages from "@/components/Messages";
import LiveDeliveryMap, { useDriverGeolocation } from "@/components/LiveDeliveryMap";
import OnboardingTracker from "@/components/OnboardingTracker";
import MemberLedger from "@/components/MemberLedger";

function geocodeFallback(city) {
  // Lightweight fallback when we don't have geocoding configured.
  // Returns deterministic pseudo-coords near US center based on city name hash so the map always renders.
  if (!city) return { lat: 37.7749, lng: -122.4194 };
  let h = 0;
  for (let i = 0; i < city.length; i++) h = (h << 5) - h + city.charCodeAt(i);
  const lat = 36 + ((Math.abs(h) % 700) / 100); // 36..43
  const lng = -100 + ((Math.abs(h >> 3) % 2500) / 100); // -100..-75
  return { lat, lng };
}

function ActiveDeliveryCard({ o, onStatus }) {
  const [driverPos, setDriverPos] = useState(o.driver_lat ? { lat: o.driver_lat, lng: o.driver_lng } : null);
  const pickup = geocodeFallback(o.pickup_city);
  const dropoff = geocodeFallback(o.dropoff_city);
  const tracking = ["accepted", "picked_up", "in_transit"].includes(o.status);

  useDriverGeolocation(tracking, async (pos) => {
    setDriverPos(pos);
    try {
      await api.post(`/orders/${o.order_id}/location`, pos);
    } catch (e) { /* ignore */ }
  });

  return (
    <div className="bg-white border border-stone-200 rounded-md p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-serif text-xl text-stone-900">{o.product_title}</div>
          <div className="text-sm text-stone-500 font-sans">Client: {o.client_name}</div>
        </div>
        <Badge data-testid={`status-badge-${o.order_id}`} className="bg-stone-800 text-stone-50">{o.status}</Badge>
      </div>
      <div className="mt-5">
        <LiveDeliveryMap pickup={pickup} dropoff={dropoff} driver={driverPos} />
        {tracking && (
          <div className="mt-2 text-xs text-emerald-800 font-sans flex items-center gap-2" data-testid={`live-indicator-${o.order_id}`}>
            <span className="w-2 h-2 rounded-full bg-emerald-700 animate-pulse" /> Live GPS sharing on — your location is updating
          </div>
        )}
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {o.status === "accepted" && <Button data-testid={`pickup-${o.order_id}`} onClick={() => onStatus(o.order_id, "picked_up")} className="bg-amber-700 hover:bg-amber-600 text-stone-50">Mark Picked Up</Button>}
        {o.status === "picked_up" && <Button data-testid={`transit-${o.order_id}`} onClick={() => onStatus(o.order_id, "in_transit")} className="bg-amber-700 hover:bg-amber-600 text-stone-50">In Transit</Button>}
        {o.status === "in_transit" && <Button data-testid={`deliver-${o.order_id}`} onClick={() => onStatus(o.order_id, "delivered")} className="bg-emerald-800 hover:bg-emerald-700 text-stone-50">Mark Delivered</Button>}
        {o.status === "delivered" && o.rating && (
          <div className="flex items-center gap-2 text-amber-700 font-sans text-sm"><Star size={16} fill="currentColor" />{o.rating}/5 — {o.feedback}</div>
        )}
      </div>
    </div>
  );
}

const MILE_RATE = 1.5;
const PER_ORDER_MIN = 2;
const PER_ORDER_MAX = 10;

export default function DriverDashboard() {
  const { user, refresh } = useAuth();
  const [orders, setOrders] = useState([]);
  const [openPayout, setOpenPayout] = useState(false);
  const [openRequest, setOpenRequest] = useState(false);
  const [payout, setPayout] = useState({ payout_paypal: user?.payout_paypal || "", payout_cashapp: user?.payout_cashapp || "" });
  const [farmers, setFarmers] = useState([]);
  const [request, setRequest] = useState({ farmer_id: "", message: "" });

  const load = async () => {
    const [o, p] = await Promise.all([
      api.get("/orders"),
      api.get("/products"),
    ]);
    setOrders(o.data);
    const map = {};
    p.data.forEach((pr) => { map[pr.farmer_id] = pr.farmer_name; });
    setFarmers(Object.entries(map).map(([id, name]) => ({ id, name })));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const accept = async (id) => {
    await api.post(`/orders/${id}/accept`);
    toast.success("Order accepted.");
    load();
  };

  const updateStatus = async (id, status) => {
    await api.put(`/orders/${id}/status`, { status });
    toast.success(`Marked ${status}.`);
    load();
  };

  const savePayout = async () => {
    await api.put("/auth/profile", payout);
    toast.success("Payout info saved.");
    setOpenPayout(false);
    refresh();
  };

  const sendRequest = async () => {
    if (!request.farmer_id || !request.message) return;
    await api.post("/driver-requests", request);
    toast.success("Request sent to farmer.");
    setOpenRequest(false);
    setRequest({ farmer_id: "", message: "" });
  };

  const available = orders.filter((o) => !o.driver_id && o.status === "placed");
  const mine = orders.filter((o) => o.driver_id === user.user_id);

  const earnings = mine.reduce((s, o) => {
    const mileagePay = o.distance_miles * MILE_RATE;
    const orderPay = Math.min(PER_ORDER_MAX, Math.max(PER_ORDER_MIN, o.distance_miles * 0.5));
    return s + (o.status === "delivered" ? mileagePay + orderPay + (o.tip || 0) : 0);
  }, 0);

  const stat = (label, value, testid) => (
    <div data-testid={testid} className="bg-white border border-stone-200 p-6 rounded-md">
      <div className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">{label}</div>
      <div className="mt-3 font-serif text-3xl text-stone-900">{value}</div>
    </div>
  );

  return (
    <div className="space-y-8">
      <OnboardingTracker />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        {stat("Available Pickups", available.length, "stat-available")}
        {stat("Active Deliveries", mine.filter((o) => o.status !== "delivered").length, "stat-active")}
        {stat("Completed", mine.filter((o) => o.status === "delivered").length, "stat-completed")}
        {stat("Earnings", `$${earnings.toFixed(2)}`, "stat-earnings")}
      </div>

      <div className="flex flex-wrap gap-3">
        <Dialog open={openPayout} onOpenChange={setOpenPayout}>
          <DialogTrigger asChild>
            <Button data-testid="payout-btn" variant="outline" className="border-stone-300">Set Payout (PayPal / CashApp)</Button>
          </DialogTrigger>
          <DialogContent className="bg-stone-50">
            <DialogHeader><DialogTitle className="font-serif text-2xl">Payout Info</DialogTitle></DialogHeader>
            <Input data-testid="payout-paypal" placeholder="PayPal email or link" value={payout.payout_paypal} onChange={(e) => setPayout({ ...payout, payout_paypal: e.target.value })} className="bg-white" />
            <Input data-testid="payout-cashapp" placeholder="CashApp $tag" value={payout.payout_cashapp} onChange={(e) => setPayout({ ...payout, payout_cashapp: e.target.value })} className="bg-white" />
            <DialogFooter><Button data-testid="payout-save" onClick={savePayout} className="bg-emerald-800 hover:bg-emerald-700 text-stone-50">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={openRequest} onOpenChange={setOpenRequest}>
          <DialogTrigger asChild>
            <Button data-testid="ask-food-btn" variant="outline" className="border-stone-300"><MessageSquarePlus size={16} className="mr-2" />Ask Hobby Farmer for Free Food</Button>
          </DialogTrigger>
          <DialogContent className="bg-stone-50">
            <DialogHeader><DialogTitle className="font-serif text-2xl">Request Free Food</DialogTitle></DialogHeader>
            <Select value={request.farmer_id} onValueChange={(v) => setRequest({ ...request, farmer_id: v })}>
              <SelectTrigger data-testid="ask-farmer" className="bg-white"><SelectValue placeholder="Choose a hobby farmer" /></SelectTrigger>
              <SelectContent>{farmers.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
            </Select>
            <Textarea data-testid="ask-message" rows={4} placeholder="A polite message..." value={request.message} onChange={(e) => setRequest({ ...request, message: e.target.value })} className="bg-white" />
            <DialogFooter><Button data-testid="ask-submit" onClick={sendRequest} className="bg-emerald-800 hover:bg-emerald-700 text-stone-50">Send Request</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs defaultValue="available">
        <div className="overflow-x-auto -mx-2 px-2">
          <TabsList className="bg-stone-100 flex-nowrap whitespace-nowrap w-max sm:w-auto">
            <TabsTrigger data-testid="tab-available" value="available">Available</TabsTrigger>
            <TabsTrigger data-testid="tab-active" value="active">My Active</TabsTrigger>
            <TabsTrigger data-testid="tab-ledger" value="ledger">Earnings</TabsTrigger>
            <TabsTrigger data-testid="tab-deposits" value="deposits">Deposits</TabsTrigger>
            <TabsTrigger data-testid="tab-messages" value="messages">Messages</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="available" className="mt-6 space-y-4">
          {available.length === 0 ? (
            <div className="bg-white border border-stone-200 p-10 text-center rounded-md font-sans text-stone-500">No pickups available right now.</div>
          ) : available.map((o) => (
            <div key={o.order_id} className="bg-white border border-stone-200 rounded-md p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <div className="font-serif text-xl text-stone-900">{o.product_title}</div>
                <div className="mt-1 text-sm text-stone-500 font-sans">{o.pickup_city} → {o.dropoff_city} · {o.distance_miles} mi</div>
              </div>
              <Button data-testid={`accept-${o.order_id}`} onClick={() => accept(o.order_id)} className="bg-emerald-800 hover:bg-emerald-700 text-stone-50">
                <Truck size={16} className="mr-2" />Accept Pickup
              </Button>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="active" className="mt-6 space-y-6">
          {mine.length === 0 ? (
            <div className="bg-white border border-stone-200 p-10 text-center rounded-md font-sans text-stone-500">No active deliveries yet.</div>
          ) : mine.map((o) => (
            <ActiveDeliveryCard key={o.order_id} o={o} onStatus={updateStatus} />
          ))}
        </TabsContent>

        <TabsContent value="ledger" className="mt-6">
          <div className="bg-white border border-stone-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-stone-100 text-stone-600 uppercase tracking-[0.15em] text-xs">
                <tr><th className="text-left p-4">Order</th><th className="text-left p-4">Distance</th><th className="text-left p-4">Mileage Pay</th><th className="text-left p-4">Order Pay</th><th className="text-left p-4">Tip</th><th className="text-left p-4">Status</th></tr>
              </thead>
              <tbody>
                {mine.map((o) => {
                  const mileagePay = o.distance_miles * MILE_RATE;
                  const orderPay = Math.min(PER_ORDER_MAX, Math.max(PER_ORDER_MIN, o.distance_miles * 0.5));
                  return (
                    <tr key={o.order_id} className="border-t border-stone-100">
                      <td className="p-4 font-sans">{o.product_title}</td>
                      <td className="p-4 font-sans text-stone-600">{o.distance_miles} mi</td>
                      <td className="p-4 font-sans text-stone-700">${mileagePay.toFixed(2)}</td>
                      <td className="p-4 font-sans text-stone-700">${orderPay.toFixed(2)}</td>
                      <td className="p-4 font-sans text-stone-700">${(o.tip || 0).toFixed(2)}</td>
                      <td className="p-4"><Badge className="bg-stone-800 text-stone-50">{o.status}</Badge></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {mine.length === 0 && <div className="p-8 text-center text-stone-500 font-sans">No deliveries yet.</div>}
          </div>
          <div className="mt-3 text-xs text-stone-500 font-sans">Earnings are calculated automatically per route. Tips are paid 100% to driver. Deposits to your PayPal/CashApp appear in the Deposits tab once admins verify the funds.</div>
        </TabsContent>

        <TabsContent value="deposits" className="mt-6">
          <MemberLedger />
        </TabsContent>

        <TabsContent value="messages" className="mt-6">
          <Messages />
        </TabsContent>
      </Tabs>
    </div>
  );
}
